import { useState } from "react";
import { useAuth } from "@aviro360/login";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppShellLayout, ClerkSignInPage } from "design-components";
import { operatorSignedInChildRoutes } from "./app/routes/app-routes.route";
import aviroMarkUrl from "@design-system-assets/aviro-logo.svg";

const OPERATOR_SIDEBAR_HREF_BY_ID = {
  start: "/",
  operators: "/operators",
  customers: "/customers",
  carriers: "/carriers",
  bookings: "/bookings",
  settings: "/settings/users",
} as const;

// SignedOut only renders when userId === null; during Clerk step transitions userId can be undefined and unmount the page.
function SignInRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  if (isLoaded && isSignedIn) {
    return <Navigate to="/" replace />;
  }
  return <ClerkSignInPage fallbackRedirectUrl="/" />;
}

function SignedInAppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [navSelection, setNavSelection] = useState("start");

  const selectedId = location.pathname.startsWith("/settings")
    ? "settings"
    : location.pathname.startsWith("/operators")
      ? "operators"
      : location.pathname.startsWith("/customers")
        ? "customers"
        : location.pathname.startsWith("/bookings")
          ? "bookings"
          : location.pathname.startsWith("/carriers")
            ? "carriers"
            : navSelection;

  const handleSidebarSelect = (id: string) => {
    if (id === "settings") {
      navigate("/settings/users");
      return;
    }
    if (id === "operators") {
      navigate("/operators");
      setNavSelection("operators");
      return;
    }
    if (id === "customers") {
      navigate("/customers");
      setNavSelection("customers");
      return;
    }
    if (id === "bookings") {
      navigate("/bookings");
      setNavSelection("bookings");
      return;
    }
    if (id === "carriers") {
      navigate("/carriers");
      setNavSelection("carriers");
      return;
    }
    if (id === "start") {
      navigate("/");
      setNavSelection("start");
      return;
    }
    if (id === "notifications") {
      setNavSelection("notifications");
      return;
    }
    setNavSelection(id);
    navigate("/");
  };

  return (
    <AppShellLayout
      sidebarProps={{
        visiblePrimaryNavIds: ["operators", "customers", "carriers", "bookings"],
        title: "Scheduler",
        logoSrc: aviroMarkUrl,
        logoAlt: "Aviro360",
        selectedId,
        onSelect: handleSidebarSelect,
        hrefById: OPERATOR_SIDEBAR_HREF_BY_ID,
        appBasePath: import.meta.env.BASE_URL,
      }}
    >
      <div className="app-layout">
        <Outlet />
      </div>
    </AppShellLayout>
  );
}

function App() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return null;
  }

  return (
    <Routes>
      <Route path="/sign-in/*" element={<SignInRoute />} />
      {isSignedIn ? (
        <Route element={<Outlet />}>
          <Route element={<SignedInAppLayout />}>{operatorSignedInChildRoutes()}</Route>
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      )}
    </Routes>
  );
}

export default App;
